import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ShoppingBag, Hammer, PackageCheck, Truck,
  IndianRupee, Wallet, CalendarClock, AlertTriangle, Activity,
  CalendarCheck, Banknote,
} from "lucide-react";
import api, { fmt } from "@/lib/api";

const TOP_CARDS = [
  { key: "orders_due_today", label: "Orders Due Today", icon: CalendarCheck, to: "/orders?filter=due-today", color: "text-teal-400" },
  { key: "karigar_pending_work", label: "Karigar Pending Work", icon: Hammer, to: "/orders?filter=with-karigar", color: "text-amber-400" },
  { key: "cash_in_hand", label: "Cash In Hand", icon: Banknote, to: "/accounts", money: true, color: "text-emerald-400" },
];

const badgeFor = (a) => {
  const m = a.message || "";
  if (a.type === "payment")
    return m.includes("paid to karigar")
      ? { label: "Karigar Paid", cls: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" }
      : { label: "Payment Received", cls: "bg-green-500/10 text-green-500 border-green-500/20" };
  if (a.type === "order") {
    if (m.includes("→ Cancelled")) return { label: "Order Cancelled", cls: "bg-red-500/10 text-red-500 border-red-500/20" };
    if (m.includes("→ With Karigar")) return { label: "Sent To Karigar", cls: "bg-orange-500/10 text-orange-500 border-orange-500/20" };
    if (m.includes("→ Delivered")) return { label: "Delivered", cls: "bg-green-500/10 text-green-500 border-green-500/20" };
    if (m.includes("→ Ready")) return { label: "Received", cls: "bg-teal-500/10 text-teal-500 border-teal-500/20" };
    if (m.includes("created")) return { label: "New Order", cls: "bg-blue-500/10 text-blue-500 border-blue-500/20" };
    return { label: "Order Update", cls: "bg-blue-500/10 text-blue-500 border-blue-500/20" };
  }
  if (a.type === "production") return { label: "Shop Production", cls: "bg-violet-500/10 text-violet-500 border-violet-500/20" };
  if (a.type === "customer") return { label: "New Customer", cls: "bg-sky-500/10 text-sky-500 border-sky-500/20" };
  if (a.type === "stock") return { label: "Stock", cls: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20" };
  if (a.type === "expense") return { label: "Expense", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" };
  return { label: "Activity", cls: "bg-muted text-muted-foreground border-transparent" };
};

const CARDS = [
  { key: "customer_orders", label: "Customer Orders", icon: ShoppingBag, to: "/orders", color: "text-purple-500" },
  { key: "with_karigar", label: "Orders With Karigar", icon: Hammer, to: "/karigars", color: "text-amber-500" },
  { key: "ready", label: "Ready Products", icon: PackageCheck, to: "/orders", color: "text-orange-500" },
  { key: "delivered", label: "Delivered Orders", icon: Truck, to: "/orders", color: "text-green-500" },
  { key: "pending_customer_payments", label: "Pending Customer Payments", icon: IndianRupee, to: "/orders", money: true, color: "text-red-500" },
  { key: "pending_karigar_payments", label: "Pending Karigar Payments", icon: Wallet, to: "/karigars", money: true, color: "text-red-400" },
  { key: "todays_deliveries", label: "Today's Deliveries", icon: CalendarClock, to: "/orders", color: "text-teal-500" },
  { key: "overdue_orders", label: "Overdue Orders", icon: AlertTriangle, to: "/orders", color: "text-rose-500" },
];

const Skeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={i} className="h-32 rounded-xl bg-muted animate-pulse" />
    ))}
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    staleTime: 15000,
  });

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Your business at a glance</p>
      </div>

      {isLoading || !data ? (
        <Skeleton />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="dashboard-top-cards">
            {TOP_CARDS.map(({ key, label, icon: Icon, to, money, color }, i) => (
              <motion.button
                key={key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04, duration: 0.3 }}
                onClick={() => navigate(to)}
                data-testid={`dashboard-card-${key.replace(/_/g, "-")}`}
                className="text-left rounded-xl border bg-card p-4 hover:border-foreground/20 transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <p className="font-heading text-xl lg:text-2xl font-semibold tracking-tight">
                    {money ? fmt(data[key]) : data[key]}
                  </p>
                  <Icon className={`h-4 w-4 ${color} opacity-80 group-hover:scale-110 transition-transform`} />
                </div>
                <p className="text-xs text-muted-foreground mt-2">{label}</p>
              </motion.button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {CARDS.map(({ key, label, icon: Icon, to, money, color }, i) => (
              <motion.button
                key={key}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03, duration: 0.3 }}
                onClick={() => navigate(to)}
                data-testid={`dashboard-card-${key.replace(/_/g, "-")}`}
                className="text-left rounded-xl border bg-card p-5 hover:border-foreground/20 transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <p className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">
                    {money ? fmt(data[key]) : data[key]}
                  </p>
                  <Icon className={`h-5 w-5 ${color} opacity-80 group-hover:scale-110 transition-transform`} />
                </div>
                <p className="text-xs lg:text-sm text-muted-foreground mt-2">{label}</p>
              </motion.button>
            ))}
          </div>

          <div className="rounded-xl border bg-card" data-testid="recent-activity">
            <div className="flex items-center gap-2 px-6 py-4 border-b">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-heading font-semibold">Recent Activity</h2>
            </div>
            {data.activity.length === 0 ? (
              <p className="px-6 py-8 text-sm text-muted-foreground">No activity yet. Add your first customer to get started.</p>
            ) : (
              <ul className="divide-y">
                {data.activity.map((a) => {
                  const b = badgeFor(a);
                  return (
                    <li key={a.id} className="px-6 py-3.5 flex items-center gap-3 text-sm">
                      <span className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${b.cls}`}>{b.label}</span>
                      <span className="flex-1 min-w-0 truncate">{a.message}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {new Date(a.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
